/*
 * qi-live-price.js — QI LITE live tier unit-price + line-total  (Workstream W1)
 * --------------------------------------------------------------------------
 * Self-contained, framework-agnostic. No dependencies. Survives a future
 * Dawn/OS2.0 migration (thin Liquid glue passes data in via window.QI_TIERS).
 *
 * Contract (emitted by product.liquid, checkout-ready products only):
 *   window.QI_TIERS      = [{ id:<variantId>, qty:<threshold>, price:<dollars> }, ...]  // ascending qty
 *   window.QI_CURRENCY   = "USD"            (optional, defaults USD)
 *   window.QI_SETUP_FEE  = 55.0             (optional, dollars; 0/absent = hide)
 *
 * Renders:
 *   #price        -> unit price for the selected tier (or "Contact for pricing")
 *   #price-total  -> unit price x quantity (line total)
 *   #qi-live-qty  -> the quantity used
 *   #qi-setup-note-> one-time set-up charge note (only when an imprint method is chosen)
 *   #qi-inquire   -> shown instead of $0 when the tier is a contact-for-pricing sentinel
 *
 * Tier selection MIRRORS the (fixed) setTierVariantIdByQty() in product.liquid:
 * the highest tier whose qty threshold <= current quantity, clamped to the
 * lowest tier when below it. This guarantees on-screen price == variant added.
 *
 * Coexistence: only reads #quantity / #imprintSelect and writes its own ids.
 * Uses addEventListener (never overwrites Bold Options / ShopStorm handlers).
 */
(function () {
  'use strict';

  function money(n) {
    var cur = (typeof window.QI_CURRENCY === 'string' && window.QI_CURRENCY) ? window.QI_CURRENCY : 'USD';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(Number(n) || 0);
    } catch (e) {
      return '$' + (Number(n) || 0).toFixed(2);
    }
  }

  function getTiers() {
    var t = Array.isArray(window.QI_TIERS) ? window.QI_TIERS.slice() : [];
    t = t.filter(function (x) { return x && isFinite(x.qty); });
    t.sort(function (a, b) { return a.qty - b.qty; });
    return t;
  }

  // highest tier with threshold <= qty; clamps to lowest tier when qty is below it
  function pickTier(list, qty) {
    var sel = list[0];
    for (var i = 0; i < list.length; i++) {
      if (list[i].qty <= qty) sel = list[i];
    }
    return sel;
  }

  function el(id) { return document.getElementById(id); }

  // Setup fee for the CURRENTLY selected imprint method, matching the cart:
  //   price  = the `Setup Fee - <METHOD>` product's price (they differ per
  //            method -- silkscreen 60, embroidery 100, and so on)
  //   charged quantity = colour count for silkscreen / hot-stamp / pad-print,
  //                      otherwise 1   (getSetupFeeCount in cart.liquid)
  // Returns 0 when no method is chosen, so "Blank" carries no fee anywhere.
  function setupFee() {
    var sel = el('imprintSelect');
    if (!sel || !sel.value) return 0;

    var map = window.QI_SETUP_FEES;
    var unit = (map && typeof map[sel.value] === 'number') ? map[sel.value] : null;
    if (unit === null) {
      // no per-method product: fall back to the flat figure rather than 0, so a
      // missing product understates nothing
      unit = (typeof window.QI_SETUP_FEE === 'number' && isFinite(window.QI_SETUP_FEE)) ? window.QI_SETUP_FEE : 0;
    }
    if (!(unit > 0)) return 0;

    var opt = (sel.selectedIndex >= 0) ? sel.options[sel.selectedIndex] : null;
    var method = ((sel.value || '') + ' ' + (opt ? (opt.text || '') : '')).toUpperCase();
    var perColour = method.indexOf('SILKSCREEN') > -1 || method.indexOf('HOT-STAMP') > -1 ||
                    method.indexOf('PAD-PRINT') > -1 || method.indexOf('HOT STAMP') > -1 ||
                    method.indexOf('PAD PRINT') > -1;

    var colors = parseInt((el('imprintColorId') || {}).value, 10);
    if (!isFinite(colors) || colors < 1) colors = 1;

    return unit * (perColour ? colors : 1);
  }

  function imprintSelected() {
    var s = el('imprintSelect');
    return !!(s && s.value && s.value !== '');
  }

  // Run charge, using the SAME multiplier rule the cart applies when it adds
  // the run-charge line item (qiRunChargeUnits, defined on the product page).
  // If either the rule or the prices are absent we return zero units rather
  // than guessing, so the total can never over-state what the cart will charge.
  function runCharge(qty) {
    var cfg = window.QI_RUN_CHARGE;
    var sel = el('imprintSelect');
    if (!cfg || !sel || typeof window.qiRunChargeUnits !== 'function') {
      return { units: 0, unitPrice: 0, total: 0 };
    }
    var opt = (sel.selectedIndex >= 0) ? sel.options[sel.selectedIndex] : null;
    var method = ((sel.value || '') + ' ' + (opt ? (opt.text || '') : '')).toUpperCase();
    if (!sel.value) return { units: 0, unitPrice: 0, total: 0 };

    var colors = parseInt((el('imprintColorId') || {}).value, 10);
    if (!isFinite(colors) || colors < 0) colors = 0;

    var units = window.qiRunChargeUnits(method, colors);
    var unitPrice = (method.indexOf('EMBROIDERY') > -1) ? cfg.embroidery : cfg.standard;
    if (!(units > 0) || !(unitPrice > 0)) return { units: 0, unitPrice: 0, total: 0 };

    return { units: units, unitPrice: unitPrice, total: unitPrice * units * qty };
  }

  function update() {
    var list = getTiers();
    // No quantity tiers on this product. The card is still rendered -- the
    // design is theme-level now -- but it shows the contact-for-pricing state
    // rather than quoting a price that does not exist. Previously this bailed
    // out and left the panel showing em-dashes.
    if (!list.length) {
      var pEl = el('price'), tEl = el('price-total'), nEl = el('qi-setup-note'), iEl = el('qi-inquire');
      if (pEl) pEl.textContent = 'Contact for pricing';
      if (tEl) tEl.textContent = '';
      if (nEl) nEl.style.display = 'none';
      if (iEl) iEl.style.display = '';
      emit({ available: false, qty: 0 });
      return;
    }

    var qInput = el('quantity');
    var qty = qInput ? parseInt(qInput.value, 10) : NaN;
    if (!isFinite(qty) || qty < 1) qty = list[0].qty;

    var tier = pickTier(list, qty);
    var priceEl = el('price');
    var totalEl = el('price-total');
    var qtyEl = el('qi-live-qty');
    var inquire = el('qi-inquire');
    var setupNote = el('qi-setup-note');

    if (qtyEl) qtyEl.textContent = qty.toLocaleString('en-US');

    // contact-for-pricing sentinel: never show $0
    if (!tier || !(Number(tier.price) > 0)) {
      if (priceEl) priceEl.textContent = 'Contact for pricing';
      if (totalEl) totalEl.textContent = '';
      if (setupNote) setupNote.style.display = 'none';
      if (inquire) inquire.style.display = '';
      emit({ available: false, qty: qty });
      return;
    }
    if (inquire) inquire.style.display = 'none';

    var unit = Number(tier.price);
    if (priceEl) priceEl.textContent = money(unit);
    if (totalEl) totalEl.textContent = money(unit * qty);

    var fee = setupFee();
    var feeApplies = fee > 0 && imprintSelected();

    if (setupNote) {
      if (feeApplies) {
        setupNote.style.display = '';
        setupNote.textContent = '+ ' + money(fee) + ' Set-up charge';
      } else {
        setupNote.style.display = 'none';
      }
    }

    // W-wins2/3/4: broadcast the computed numbers so companion modules (itemised
    // total, CTA price label, delivery promise) never re-implement tier maths.
    var rc = runCharge(qty);

    emit({
      available: true,
      qty: qty,
      unit: unit,
      lineTotal: unit * qty,
      setupFee: feeApplies ? fee : 0,
      runCharge: rc.total,
      runChargeUnits: rc.units,
      runChargeUnitPrice: rc.unitPrice,
      grandTotal: unit * qty + (feeApplies ? fee : 0) + rc.total,
      tierQty: Number(tier.qty),
      currency: (typeof window.QI_CURRENCY === 'string' && window.QI_CURRENCY) ? window.QI_CURRENCY : 'USD'
    });
  }

  function emit(detail) {
    try {
      document.dispatchEvent(new CustomEvent('qi:price', { detail: detail }));
    } catch (e) {
      /* CustomEvent unsupported — companions simply stay on their placeholders */
    }
  }

  function bind() {
    // imprint method / colour count now feed the total via the run charge
    ['imprintSelect', 'imprintColorId'].forEach(function (id) {
      var e = el(id);
      if (!e) return;
      e.addEventListener('change', update);
      e.addEventListener('input', update);
    });
    var q = el('quantity');
    if (q) {
      q.addEventListener('input', update);
      q.addEventListener('change', update);
    }
    var imp = el('imprintSelect');
    if (imp) imp.addEventListener('change', update);
    update();
  }

  // public API (also lets the legacy priceUpdate() shim delegate here)
  window.QILivePrice = { update: update, pickTier: pickTier };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
